import { type FC, useCallback, useEffect } from "react";
import { type IntlShape, useIntl } from "react-intl";
import { useToken } from "@packsize/authentication";

import { baseRoutes } from "@/api/base-routes";
import { useEmMachine } from "@/hooks/machines/em";
import { useSignalRHub } from "@/hooks/use-signalr-hub";
import type { ErrorsAndWarningsProps, MachineFaultDisplay } from "@/schemas";

const websocketUrl = `${baseRoutes.em}/hubs/emMachines`;
const errorChangedEventName = "OnErrorChanged";

const OUT_OF_CORRUGATE_ERROR = 5;

type EmError = {
	code: number;
	arguments: (string | number)[];
};

type EmErrorDisplay = {
	translationKey: string;
	resolutionArguments: Record<string, number | string>;
};

const getResolutionArguments = (code: number, errorArguments: (string | number)[]): Record<string, string | number> => {
	if (code === OUT_OF_CORRUGATE_ERROR) {
		return {
			trackNumber: errorArguments[0],
		};
	}

	return {};
};

function createError(code: number, errorArguments: (string | number)[]): EmErrorDisplay {
	return {
		translationKey: `Em Error ${code}`,
		resolutionArguments: getResolutionArguments(code, errorArguments),
	};
}

function createDisplayItem(
	machineId: string,
	code: number,
	errorArguments: (string | number)[],
	intl: IntlShape,
): MachineFaultDisplay {
	const item = createError(code, errorArguments);

	return {
		machineId,
		title: intl.formatMessage(
			{ id: item.translationKey, defaultMessage: "{translationKey}" },
			{ translationKey: item.translationKey, ...item.resolutionArguments },
		),
		image: `${import.meta.env?.VITE_BASENAME}/images/errors-and-warnings/em/error/default.svg`,
		type: "Error",
	};
}

interface EmMachineErrorsAndWarningsProps {
	readonly machineId: string;
	readonly onErrorsChanged: ErrorsAndWarningsProps["onErrorsChanged"];
}

const EmMachineErrorsAndWarnings = ({ machineId, onErrorsChanged }: EmMachineErrorsAndWarningsProps) => {
	const intl = useIntl();
	const { bearerToken } = useToken();
	const { data: machine } = useEmMachine(machineId);
	const { hubConnection, subscribe, unsubscribe } = useSignalRHub({
		hubUrl: websocketUrl,
		token: bearerToken,
	});

	const notifyErrors = useCallback(
		(error: EmError | undefined) => {
			if (error === undefined) {
				onErrorsChanged(machineId, []);
				return;
			}

			if (error.code === 0) {
				onErrorsChanged(machineId, []);
				return;
			}

			onErrorsChanged(machineId, [createDisplayItem(machineId, error.code, error.arguments, intl)]);
		},
		[intl, machineId, onErrorsChanged],
	);

	useEffect(() => {
		if (machine === undefined) {
			return;
		}

		if (machine === null || machine.stopReason.isError === false) {
			notifyErrors(undefined);
			return;
		}

		notifyErrors({
			code: machine.stopReason.stopCode,
			arguments: machine.stopReason.arguments,
		});
	}, [machine, notifyErrors]);

	useEffect(() => {
		if (!hubConnection) {
			return;
		}

		const handleErrorChanged = (...args: unknown[]) => {
			const [_tenantId, eventMachineId, error] = args as [string, string, EmError | undefined];

			if (eventMachineId !== machineId) {
				return;
			}

			notifyErrors(error);
		};

		subscribe(errorChangedEventName, handleErrorChanged);

		return () => {
			unsubscribe(errorChangedEventName, handleErrorChanged);
		};
	}, [hubConnection, machineId, notifyErrors, subscribe, unsubscribe]);

	return null;
};

export const EmErrorsAndWarnings: FC<ErrorsAndWarningsProps> = ({ machines, onErrorsChanged }) => {
	return (
		<>
			{machines
				.filter(({ machineType: configuredMachineType }) => configuredMachineType === "Em")
				.map(({ machineId }) => (
					<EmMachineErrorsAndWarnings key={machineId} machineId={machineId} onErrorsChanged={onErrorsChanged} />
				))}
		</>
	);
};
